import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Shield, CheckCircle2, AlertCircle, RefreshCw, Clock,
} from 'lucide-react';
import { api } from '@/utils/api';

// OTP verification page.
// Norman's rule: the OTP is 6 digits, numeric only, auto-advances between
// boxes, and on success we show a LARGE green success card (not a toast).

const OTP_LEN = 6;
const MAX_ATTEMPTS = 3;

interface TicketLite {
  id: string;
  number: string;
  sopStage?: string;
  serviceCategory?: string;
  productUnit?: {
    id: string;
    code: string;
    warrantyEndDate?: string | null;
  } | null;
}

interface VerifyResult {
  ok: true;
  ticketClosed: boolean;
  warrantyActivated?: boolean;
  warrantyEndDate?: string;
  productName?: string;
}

export function OtpVerifyPage() {
  const { id: ticketId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [ticket, setTicket] = useState<TicketLite | null>(null);
  const [digits, setDigits] = useState<string[]>(Array(OTP_LEN).fill(''));
  const [attempts, setAttempts] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<VerifyResult | null>(null);
  const [cooldown, setCooldown] = useState(0);

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const fetchTicket = useCallback(async () => {
    if (!ticketId) return;
    try {
      const res = await api.get(`/service-tickets/${ticketId}`);
      setTicket(res.data.data?.ticket ?? res.data.data ?? res.data);
    } catch {
      // non-fatal — still allow typing OTP
    }
  }, [ticketId]);

  useEffect(() => { fetchTicket(); }, [fetchTicket]);

  // Cooldown countdown for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    // Focus first empty box on mount
    const firstEmpty = digits.findIndex(d => !d);
    if (firstEmpty >= 0) inputRefs.current[firstEmpty]?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDigit = (idx: number, val: string) => {
    if (result) return; // already verified
    const cleaned = val.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[idx] = cleaned;
    setDigits(next);
    setError(null);
    if (cleaned && idx < OTP_LEN - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
    // Auto-submit when all filled
    if (cleaned && idx === OTP_LEN - 1 && next.every(d => d)) {
      verify(next.join(''));
    }
  };

  const handleKeyDown = (idx: number, ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (ev.key === 'ArrowLeft' && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (ev.key === 'ArrowRight' && idx < OTP_LEN - 1) {
      inputRefs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (ev: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = ev.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LEN);
    if (!pasted) return;
    ev.preventDefault();
    const next = Array(OTP_LEN).fill('');
    for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
    setDigits(next);
    if (pasted.length === OTP_LEN) verify(pasted);
    else inputRefs.current[pasted.length]?.focus();
  };

  const verify = async (otp: string) => {
    if (!ticketId || otp.length !== OTP_LEN) return;
    if (attempts >= MAX_ATTEMPTS) {
      setError('Max attempts reached. Ask customer to request a new OTP.');
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const res = await api.post(`/service/tickets/${ticketId}/otp/verify`, { otp });
      const data = res.data.data ?? res.data;
      setResult({
        ok: true,
        ticketClosed: true,
        warrantyActivated: data.warrantyActivated ?? data.serviceCategory === 'installation',
        warrantyEndDate: data.warrantyEndDate ?? ticket?.productUnit?.warrantyEndDate ?? undefined,
        productName: data.productName ?? undefined,
      });
    } catch (err) {
      setAttempts(a => a + 1);
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Incorrect OTP';
      setError(`${msg}. ${MAX_ATTEMPTS - attempts - 1} attempts left.`);
      setDigits(Array(OTP_LEN).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const resend = async () => {
    if (!ticketId || cooldown > 0) return;
    setResending(true);
    setError(null);
    try {
      await api.post(`/service/tickets/${ticketId}/otp/resend`);
      setCooldown(30);
      setAttempts(0);
      setDigits(Array(OTP_LEN).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      const msg = ((err as { response?: { data?: { error?: string } } }).response?.data?.error)
        || 'Could not resend OTP';
      setError(msg);
    } finally {
      setResending(false);
    }
  };

  // SUCCESS VIEW — big green card, Norman-approved
  if (result) {
    return (
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(`/service-tickets/${ticketId}`)}
            className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            aria-label="Back"
          >
            <ArrowLeft size={18} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Ticket Closed</h1>
        </div>

        <div className="rounded-2xl border-2 border-green-400 bg-green-50 p-8 text-center dark:border-green-700 dark:bg-green-950">
          <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-green-500">
            <CheckCircle2 size={48} className="text-white" />
          </div>
          <h2 className="mb-2 text-3xl font-bold text-green-900 dark:text-green-100">
            OTP Verified
          </h2>
          <p className="mb-4 text-base text-green-800 dark:text-green-300">
            Ticket {ticket?.number || ticketId} has been closed successfully.
          </p>

          {result.warrantyActivated && result.warrantyEndDate && (
            <div className="mx-auto mt-6 max-w-md rounded-xl border-2 border-green-300 bg-white p-5 dark:border-green-700 dark:bg-green-900/30">
              <div className="mb-1 flex items-center justify-center gap-2 text-sm font-semibold text-green-800 dark:text-green-300">
                <Shield size={16} />
                WARRANTY ACTIVATED
              </div>
              <div className="text-lg font-bold text-green-900 dark:text-green-100">
                {result.productName || 'Product'}
              </div>
              <div className="mt-1 text-sm text-green-700 dark:text-green-300">
                Warranty active until{' '}
                <strong>
                  {new Date(result.warrantyEndDate).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </strong>
              </div>
            </div>
          )}

          <div className="mt-8 flex justify-center gap-2">
            <button
              onClick={() => navigate('/service-tickets')}
              className="rounded-lg bg-green-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-green-700"
            >
              Back to tickets
            </button>
          </div>
        </div>
      </div>
    );
  }

  const lockedOut = attempts >= MAX_ATTEMPTS;

  return (
    <div className="mx-auto max-w-xl">
      <div className="mb-6 flex items-center gap-3">
        <button
          onClick={() => navigate(`/service-tickets/${ticketId}`)}
          className="rounded-lg border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          aria-label="Back"
        >
          <ArrowLeft size={18} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Verify OTP</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Customer received a 6-digit code on WhatsApp.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
          <Shield size={16} className="text-[var(--ink-0)]" />
          Ticket <span className="font-mono font-semibold">{ticket?.number || ticketId}</span>
        </div>

        {/* 6-box input */}
        <div className="my-6 flex justify-center gap-2">
          {digits.map((d, idx) => (
            <input
              key={idx}
              ref={el => (inputRefs.current[idx] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={e => handleDigit(idx, e.target.value)}
              onKeyDown={e => handleKeyDown(idx, e)}
              onPaste={idx === 0 ? handlePaste : undefined}
              disabled={verifying || lockedOut}
              className={`h-14 w-12 rounded-lg border-2 text-center text-2xl font-bold focus:outline-none focus:ring-2 ${
                error
                  ? 'border-red-400 bg-red-50 text-red-700 focus:ring-red-200 dark:bg-red-950 dark:text-red-300'
                  : 'border-gray-300 bg-white text-gray-900 focus:border-[var(--ink-0)] focus:ring-[var(--ink-3)] dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100'
              } disabled:cursor-not-allowed disabled:opacity-50`}
              aria-label={`OTP digit ${idx + 1}`}
            />
          ))}
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            {error}
          </div>
        )}

        {verifying && (
          <div className="mb-3 flex items-center justify-center gap-2 text-sm text-gray-500">
            <Loader2 size={14} className="animate-spin" /> Verifying…
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <button
            onClick={() => verify(digits.join(''))}
            disabled={verifying || lockedOut || digits.some(d => !d)}
            className="flex items-center justify-center gap-1.5 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-semibold text-white hover:bg-[var(--ink-1)] disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {verifying ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
            Verify
          </button>
          <button
            onClick={resend}
            disabled={resending || cooldown > 0}
            className="flex items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            {resending ? (
              <Loader2 size={14} className="animate-spin" />
            ) : cooldown > 0 ? (
              <Clock size={14} />
            ) : (
              <RefreshCw size={14} />
            )}
            {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend OTP'}
          </button>
        </div>

        <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
          Attempts: {attempts} / {MAX_ATTEMPTS} · OTP expires 15 minutes after send.
        </div>
      </div>
    </div>
  );
}
